import SparkMD5 from "spark-md5"

import type { IntakeFileProgress, PublicIntakeFormUpload } from "./api"

interface UploadReservation {
  id: string
  direct_upload: { url: string; headers: Record<string, string> }
}

interface UploadStatus extends Partial<PublicIntakeFormUpload> {
  status: "pending" | "verifying" | "verified" | "failed"
  error?: string
}

export interface IntakeUploadMetadata {
  filename: string
  byte_size: number
  checksum: string
  content_type: string
}

let activeUploads = 0
const waitingUploads: Array<() => void> = []

class IntakeFileUploadError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "IntakeFileUploadError"
  }
}

async function checksum(file: File) {
  const digest = new SparkMD5.ArrayBuffer()
  for (let offset = 0; offset < file.size; offset += 2 * 1024 * 1024) {
    digest.append(await file.slice(offset, offset + 2 * 1024 * 1024).arrayBuffer())
  }
  return btoa(digest.end(true))
}

function uploadToStorage(
  file: File,
  reservation: UploadReservation,
  onProgress?: (progress: IntakeFileProgress) => void
) {
  return new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open("PUT", reservation.direct_upload.url)
    Object.entries(reservation.direct_upload.headers).forEach(([name, value]) => request.setRequestHeader(name, value))
    let inactivityTimer: ReturnType<typeof setTimeout> | undefined
    let uploadedBytes = 0
    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimer)
      inactivityTimer = setTimeout(
        () => {
          reject(new IntakeFileUploadError("File upload stalled for two minutes. Please try again."))
          request.abort()
        },
        2 * 60 * 1000
      )
    }
    request.upload.onprogress = (event) => {
      if (event.loaded > uploadedBytes) {
        uploadedBytes = event.loaded
        resetInactivityTimer()
      }
      if (event.lengthComputable) {
        onProgress?.({ stage: "uploading", percent: Math.min(100, Math.round((event.loaded / event.total) * 100)) })
      }
    }
    request.onload = () => {
      clearTimeout(inactivityTimer)
      if (request.status >= 200 && request.status < 300) resolve()
      else reject(new IntakeFileUploadError(`File upload failed (${request.status}). Please try again.`))
    }
    request.onerror = () => {
      clearTimeout(inactivityTimer)
      reject(new IntakeFileUploadError("File upload failed. Check your connection and try again."))
    }
    request.onabort = () => {
      clearTimeout(inactivityTimer)
      reject(new IntakeFileUploadError("File upload was interrupted. Please try again."))
    }
    resetInactivityTimer()
    try {
      request.send(file)
    } catch (error) {
      clearTimeout(inactivityTimer)
      reject(error)
    }
  })
}

// Drafts may emit all selected files at once; bound transfers across forms too.
export async function uploadIntakeFileDirectly(
  file: File,
  reserve: (metadata: IntakeUploadMetadata) => Promise<UploadReservation>,
  verify: (id: string, method: "PATCH" | "DELETE") => Promise<UploadStatus>,
  onProgress?: (progress: IntakeFileProgress) => void
): Promise<PublicIntakeFormUpload> {
  onProgress?.({ stage: "waiting", percent: 0 })
  if (activeUploads >= 3) await new Promise<void>((resolve) => waitingUploads.push(resolve))
  else activeUploads += 1

  let reservation: UploadReservation | undefined
  try {
    reservation = await reserve({
      filename: file.name,
      byte_size: file.size,
      checksum: await checksum(file),
      content_type: file.type || "application/octet-stream",
    })
    for (let attempt = 0; ; attempt += 1) {
      try {
        onProgress?.({ stage: "uploading", percent: 0 })
        await uploadToStorage(file, reservation, onProgress)
        break
      } catch (error) {
        if (attempt >= 2) throw error
        await new Promise((resolve) => setTimeout(resolve, 1000 * 2 ** attempt))
      }
    }

    onProgress?.({ stage: "verifying", percent: 100 })
    const status = await verify(reservation.id, "PATCH")
    if (status.status !== "verified")
      throw new IntakeFileUploadError(status.error || "File verification failed. Please try again.")
    if (!status.signed_id) throw new Error("File verification did not return an upload reference.")
    return {
      signed_id: status.signed_id,
      filename: status.filename || file.name,
      byte_size: status.byte_size ?? file.size,
      content_type: status.content_type,
    }
  } catch (error) {
    // Preserve the original error if cancellation also loses its connection.
    if (reservation) await verify(reservation.id, "DELETE").catch(() => undefined)
    throw error
  } finally {
    const next = waitingUploads.shift()
    if (next) next()
    else activeUploads -= 1
  }
}
